import { useCallback, useMemo } from 'react';
import { Select, SelectLayout } from '@amzn/stencil-react-components/select';

/**
 * Threshold above which Stencil recommends enabling the Select's
 * search field ("Don't use the searchable variation for less than ten
 * items"). Previously duplicated as `SEARCHABLE_SELECT_THRESHOLD` /
 * `SEARCHABLE_THRESHOLD` in three separate components.
 */
const SEARCHABLE_SELECT_THRESHOLD = 10;

/**
 * The option shape every v2 Select converges on. Kept deliberately
 * narrow — `valueAccessor="value"` means Stencil only ever reads
 * `value`, and `label` is what the custom renderers display. Callers
 * whose domain objects differ (locales, reasons, templates) map into
 * this shape before passing them in.
 */
export interface OptionSelectItem {
  readonly value: string;
  readonly label: string;
}

export interface OptionSelectProps {
  /**
   * Options to offer, in render order. Callers should memoise this
   * array: Stencil's Select compares the selected option by
   * REFERENCE, so a fresh array each render makes the trigger fall
   * back to the placeholder even while a value is selected.
   */
  readonly options: readonly OptionSelectItem[];
  /**
   * Currently-selected option's `value`. When it matches no option
   * the Select renders its placeholder — a safe "unset" state rather
   * than a crash, which is what a deactivated or cross-division
   * option looks like on read-back.
   */
  readonly value: string | undefined;
  /** Receives the picked option's `value`. Never fires with empty. */
  readonly onChange: (value: string) => void;
  readonly placeholder?: string | undefined;
  readonly disabled?: boolean | undefined;
  /** Renders the Select's error border and sets `aria-invalid`. */
  readonly hasError?: boolean | undefined;
  readonly ariaLabel?: string | undefined;
  /** Sets `aria-required` on the control. Visual asterisk is {@link RequiredLabel}'s job. */
  readonly isRequired?: boolean | undefined;
  readonly dataTestId?: string | undefined;
}

/**
 * The shared v2 Stencil `Select` configuration: `SelectLayout.Custom`,
 * `valueAccessor="value"`, matching option/selected-value renderers,
 * the searchable-at-ten threshold, and the reference-identity lookup
 * for the selected option.
 *
 * Deliberately renders NO label. Callers that need one pair this with
 * {@link RequiredLabel}, which lets a caller put the label in a `Row`
 * beside something else (a save indicator, for example) instead of
 * being forced into a label-above-control column.
 *
 * Extracted at the fourth caller. Every consumer had independently
 * re-derived the same six props plus the `{...(selected ? {value} : {})}`
 * conditional spread — the spread exists because
 * `exactOptionalPropertyTypes` rejects an explicit `undefined` for
 * Stencil's optional `value`, and getting it wrong silently pins the
 * trigger to the placeholder. One copy means one place to be right.
 */
export function OptionSelect(props: OptionSelectProps): JSX.Element {
  const {
    options,
    value,
    onChange,
    placeholder,
    disabled,
    hasError,
    ariaLabel,
    isRequired,
    dataTestId,
  } = props;

  // Plain lookup with NO truthiness guard on `value`. An EMPTY STRING
  // is a legitimate selection, not "nothing selected": the NA-reason
  // picker uses `{ reasonId: '', label }` as its sentinel for "we know
  // the label but lost the id" and prepends a synthetic option keyed on
  // `''` to keep that label on screen. Short-circuiting on falsy
  // `value` skipped that option and silently showed the placeholder,
  // which read to the auditor as their saved reason vanishing.
  //
  // `find` already returns undefined when nothing matches, so the guard
  // bought nothing even for the undefined case.
  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  const renderLabel = useCallback(
    (option: unknown): string => (option as OptionSelectItem).label,
    [],
  );

  const handleChange = useCallback(
    (option: unknown): void => {
      // Stencil types the callback as `(option: unknown) => void`.
      // Narrow, then guard the theoretical null clear — the Custom
      // layout doesn't emit null today, but one branch is cheaper
      // than an undefined leaking into a caller's state.
      const picked = option as OptionSelectItem | null | undefined;
      if (picked?.value) onChange(picked.value);
    },
    [onChange],
  );

  // Conditional spreads throughout: `exactOptionalPropertyTypes`
  // refuses an explicit `undefined` for these optional props, so the
  // keys are omitted entirely when the caller didn't supply them.
  return (
    <Select
      options={options as OptionSelectItem[]}
      layout={SelectLayout.Custom}
      onChange={handleChange}
      valueAccessor="value"
      renderOption={renderLabel}
      renderSelectedValue={renderLabel}
      isSearchable={options.length >= SEARCHABLE_SELECT_THRESHOLD}
      {...(selectedOption ? { value: selectedOption } : {})}
      {...(placeholder !== undefined ? { placeholder } : {})}
      {...(disabled !== undefined ? { disabled } : {})}
      {...(hasError !== undefined ? { error: hasError } : {})}
      {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
      {...(isRequired !== undefined ? { 'aria-required': isRequired } : {})}
      {...(hasError !== undefined ? { 'aria-invalid': hasError } : {})}
      {...(dataTestId !== undefined ? { 'data-testid': dataTestId } : {})}
    />
  );
}
